import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast } from 'react-toastify';
import EventBus from "../common/EventBus";
import adminService from "../services/admin.service";
import pkClientService from "../services/pkClient.service";
import EstimateFilePathManager from "./EstimateFilePathManager";
import ClientNetworkManager from "./ClientNetworkManager";
import { Modal, Button, Table, Form } from 'react-bootstrap';

const PKClientManager = () => {
    const { user: currentUser } = useSelector((state) => state.auth);

    const [searchTerm, setSearchTerm] = useState("");
    const [clients, setClients] = useState([]);

    const [selectedClientId, setSelectedClientId] = useState(null);
    const [clientData, setClientData] = useState(null);
    const [originalClientData, setOriginalClientData] = useState(null);

    // Reference Data
    const [clientStatuses, setClientStatuses] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);

    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    // Notification State
    const [showNotifModal, setShowNotifModal] = useState(false);
    const [clientNotifs, setClientNotifs] = useState([]);
    const [allNotifs, setAllNotifs] = useState([]);
    const [notifsLoading, setNotifsLoading] = useState(false);
    const [notificationEmails, setNotificationEmails] = useState({});
    const [submittingAction, setSubmittingAction] = useState(null);

    useEffect(() => {
        if (!currentUser || !currentUser.roles.includes("ROLE_ADMIN")) {
            return;
        }

        // Fetch reference data for dropdowns
        pkClientService.getReferenceData().then(
            (res) => {
                setClientStatuses(res.data.clientStatuses || []);
                setSubscriptions(res.data.subscriptions || []);
            },
            (err) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to load reference data for dropdowns." });
            }
        );

        // Fetch all notifications for the dropdown
        adminService.getAllNotifications().then(
            (res) => setAllNotifs(res.data),
            (err) => console.error("Failed to load notifications list")
        );
    }, [currentUser]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            searchClients();
        }
    };

    const searchClients = () => {
        if (!searchTerm || searchTerm.trim().length === 0) {
            toast.warning("Please enter a client name to search.");
            return;
        }

        if (searchTerm.trim().length < 3) {
            toast.warning("Search term must be at least 3 characters long.");
            return;
        }

        setSearchLoading(true);
        adminService.getClients().then(
            (response) => {
                const filtered = response.data.filter(client =>
                    client.clientName.toLowerCase().includes(searchTerm.toLowerCase())
                );

                setClients(filtered);
                setSearchLoading(false);

                if (filtered.length === 1) {
                    selectClient(filtered[0].oid, filtered[0].clientName);
                } else if (filtered.length === 0) {
                    toast.warning("No clients found matching that name.");
                } else {
                    setShowModal(true);
                }
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Error fetching clients." });
                setSearchLoading(false);
            }
        );
    };

    const selectClient = (clientId, clientName) => {
        setSelectedClientId(clientId);
        setSearchTerm(clientName);
        setShowModal(false);
        fetchClientDetails(clientId);
    };

    const fetchClientDetails = (clientId) => {
        setLoading(true);
        pkClientService.getClientDetails(clientId).then(
            (res) => {
                setClientData(res.data);
                setOriginalClientData(JSON.parse(JSON.stringify(res.data)));
                setLoading(false);
            },
            (err) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to load PK Client details." });
                setLoading(false);
            }
        );
    };

    const handleFieldChange = (field, value) => {
        setClientData(prev => ({
            ...prev,
            [field]: value
        }));
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const copyTextToClipboard = async (value, label) => {
        if (!value) return;

        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(value);
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = value;
                textArea.setAttribute("readonly", "");
                textArea.style.position = "fixed";
                textArea.style.left = "-9999px";
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
            }

            toast.success(`${label} copied to clipboard`);
        } catch (error) {
            console.error("Failed to copy to clipboard:", error);
            toast.error(`Failed to copy ${label}`);
        }
    };

    const renderCopyButton = (value, label) => (
        <button
            type="button"
            className="btn border-0 bg-transparent text-secondary px-2"
            onClick={() => copyTextToClipboard(value, label)}
            disabled={!value}
            title={`Copy ${label}`}
            aria-label={`Copy ${label}`}
            style={{ boxShadow: "none" }}
        >
            <i className="bi bi-copy"></i>
        </button>
    );

    const resetClientDetails = () => {
        if (originalClientData) {
            setClientData(JSON.parse(JSON.stringify(originalClientData)));
            setValidationErrors({});
            toast.info("Unsaved changes have been reset.");
        }
    };

    const saveClientDetails = () => {
        if (!selectedClientId || !clientData) return;

        let errors = {};
        if (clientData.shareInventory) {
            if (!clientData.pdContactPerson || !clientData.pdContactPerson.trim()) {
                errors.pdContactPerson = "Required when Share Inventory is enabled.";
            }
            if (!clientData.pdContactPhone || !clientData.pdContactPhone.trim()) {
                errors.pdContactPhone = "Required when Share Inventory is enabled.";
            }
            if (!clientData.pdContactEmail || !clientData.pdContactEmail.trim()) {
                errors.pdContactEmail = "Required when Share Inventory is enabled.";
            }
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Please fill in all required fields." });
            return;
        }

        setValidationErrors({});

        setLoading(true);
        pkClientService.updateClientDetails(selectedClientId, clientData).then(
            (res) => {
                toast.success("Client details updated successfully.");
                setClientData(res.data); // Update with refreshed data
                setOriginalClientData(JSON.parse(JSON.stringify(res.data)));
                setLoading(false);
            },
            (err) => {
                const _content = (err.response && err.response.data) || err.message;
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to update client details: " + _content });
                setLoading(false);
            }
        );
    };

    // Notification Logic
    const openNotifModal = () => {
        setClientNotifs([]);
        setShowNotifModal(true);
        fetchClientNotifications(selectedClientId);
    };

    const fetchClientNotifications = (clientId) => {
        setNotifsLoading(true);
        adminService.getClientNotifications(clientId).then(
            (response) => {
                setClientNotifs(response.data);
                setNotifsLoading(false);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to fetch client notifications: " + (error.response?.data || error.message) });
                setNotifsLoading(false);
            }
        );
    };

    const addNotification = (notifId) => {
        const email = notificationEmails[notifId] || "";
        if (!email.trim()) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Please provide at least one email address." });
            return;
        }
        setSubmittingAction(`add-notif-${notifId}`);
        adminService.addClientNotification(selectedClientId, notifId, email).then(
            () => {
                toast.success("Notification added to client");
                const notifToAdd = allNotifs.find(n => n.id === notifId);
                if (notifToAdd) {
                    setClientNotifs(prev => [...prev, { ...notifToAdd, email }]);
                }
                setNotificationEmails(prev => ({ ...prev, [notifId]: "" }));
                setSubmittingAction(null);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to add notification: " + (error.response?.data || error.message) });
                setSubmittingAction(null);
            }
        );
    };

    const removeNotification = (notifId) => {
        setSubmittingAction(`remove-notif-${notifId}`);
        adminService.removeClientNotification(selectedClientId, notifId).then(
            () => {
                toast.success("Notification removed from client");
                setClientNotifs(prev => prev.filter(n => n.id !== notifId));
                setSubmittingAction(null);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to remove notification: " + (error.response?.data || error.message) });
                setSubmittingAction(null);
            }
        );
    };

    if (!currentUser) return <Navigate to="/login" />;
    if (!currentUser.roles.includes("ROLE_ADMIN")) {
        return (
            <div className="container">
                <header className="jumbotron">
                    <h3><strong>Unauthorized:</strong> You must be an administrator to access this page.</h3>
                </header>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4 px-4 px-lg-5 mb-5">
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h2 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>PK Client Management</h2>
                {(loading || searchLoading) && <span className="spinner-border text-primary" role="status"></span>}
            </div>

            {/* Search Box */}
            <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                <div className="card-body p-4">
                    <div className="row align-items-end">
                        <div className="col-md-10">
                            <label htmlFor="clientSearch" className="form-label">Search Client (Admin)</label>
                            <input
                                type="text"
                                className="form-control"
                                id="clientSearch"
                                placeholder="Enter client name (min 3 chars)..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                onKeyPress={handleSearchKeyPress}
                            />
                        </div>
                        <div className="col-md-2">
                            <button
                                className="btn-premium w-100 py-2 rounded"
                                onClick={searchClients}
                                disabled={searchLoading || searchTerm.trim().length < 3}
                            >
                                {searchLoading ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-search me-2"></i>Search</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Editor Form */}
            {clientData && selectedClientId && (
                <div className="card border-0 shadow-lg mt-4" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                    <div className="card-header border-bottom-0 py-3" style={{ background: 'var(--primary-gradient)', color: 'white' }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 fw-bold">
                                <i className="bi bi-building me-2"></i>
                                Client Configuration: {clientData.clientName}
                            </h4>
                        <div className="d-flex align-items-center">
                            <Button variant="light" size="sm" className="me-3 fw-bold text-primary shadow-sm" onClick={openNotifModal} style={{ borderRadius: '8px' }}>
                                <i className="bi bi-bell-fill me-1"></i> Notifications
                            </Button>
                            <div className="text-end" style={{ fontSize: '0.8rem' }}>
                                {clientData.createdAt && <div><small className="opacity-75">Created: {new Date(clientData.createdAt).toLocaleString()}</small></div>}
                                {clientData.updatedAt && <div><small className="opacity-75">Updated: {new Date(clientData.updatedAt).toLocaleString()}</small></div>}
                            </div>
                        </div>
                        </div>
                    </div>
                    <div className="card-body p-4 p-md-5">

                        <h5 className="text-secondary border-bottom pb-2 mb-3">General Information</h5>
                        <div className="row mb-3">
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Client ID (OID)</label>
                                <div className="input-group">
                                    <input type="text" className="form-control bg-light" value={clientData.oid || ''} readOnly />
                                    {renderCopyButton(clientData.oid, "Client ID")}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Client Name</label>
                                <div className="input-group">
                                    <input type="text" className="form-control" value={clientData.clientName || ''} onChange={(e) => handleFieldChange("clientName", e.target.value)} />
                                    {renderCopyButton(clientData.clientName, "Client Name")}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Contact Person</label>
                                <input type="text" className="form-control" value={clientData.contactPerson || ''} onChange={(e) => handleFieldChange("contactPerson", e.target.value)} />
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">Email</label>
                                <div className="input-group">
                                    <input type="email" className="form-control" value={clientData.email || ''} onChange={(e) => handleFieldChange("email", e.target.value)} />
                                    {renderCopyButton(clientData.email, "Email")}
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Phone</label>
                                <input type="text" className="form-control" value={clientData.phone || ''} onChange={(e) => handleFieldChange("phone", e.target.value)} />
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">Address 1</label>
                                <input type="text" className="form-control" value={clientData.address1 || ''} onChange={(e) => handleFieldChange("address1", e.target.value)} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Address 2</label>
                                <input type="text" className="form-control" value={clientData.address2 || ''} onChange={(e) => handleFieldChange("address2", e.target.value)} />
                            </div>
                        </div>

                        <div className="row mb-4">
                            <div className="col-md-3">
                                <label className="form-label">City</label>
                                <input type="text" className="form-control" value={clientData.city || ''} onChange={(e) => handleFieldChange("city", e.target.value)} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Province Code</label>
                                <input type="text" className="form-control" value={clientData.provinceCode || ''} onChange={(e) => handleFieldChange("provinceCode", e.target.value)} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Post Code</label>
                                <input type="text" className="form-control" value={clientData.postCode || ''} onChange={(e) => handleFieldChange("postCode", e.target.value)} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Country Code</label>
                                <input type="text" className="form-control" value={clientData.countryCode || ''} onChange={(e) => handleFieldChange("countryCode", e.target.value)} />
                            </div>
                        </div>


                        <h5 className="text-secondary border-bottom pb-2 mb-3 mt-4">Operational Status</h5>
                        <div className="row mb-4">
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Client Status</label>
                                <select className="form-select" value={clientData.clientStatusId || ''} onChange={(e) => handleFieldChange("clientStatusId", parseInt(e.target.value))}>
                                    <option value="">Select Status...</option>
                                    {clientStatuses.map(s => (
                                        <option key={s.id} value={s.id}>{s.clientStatus}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Owner User Email</label>
                                <input type="text" className="form-control bg-light" value={clientData.ownerEmail || 'Not Assigned'} readOnly />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Subscription Type</label>
                                <select className="form-select" value={clientData.subscriptionId || ''} onChange={(e) => handleFieldChange("subscriptionId", parseInt(e.target.value))}>
                                    <option value="">Select Subscription...</option>
                                    {subscriptions.map(s => (
                                        <option key={s.id} value={s.id}>{s.subscriptionName}</option>
                                    ))}
                                </select>
                            </div>
                        </div>


                        <h5 className="text-secondary border-bottom pb-2 mb-3 mt-4">PartsKick Settings</h5>
                        <div className="row mb-3">

                            <div className="col-md-3">
                                <label className="form-label">Parts Dept Contact Person</label>
                                <input type="text" className={`form-control ${validationErrors.pdContactPerson ? 'is-invalid' : ''}`} value={clientData.pdContactPerson || ''} onChange={(e) => handleFieldChange("pdContactPerson", e.target.value)} />
                                {validationErrors.pdContactPerson && <div className="invalid-feedback">{validationErrors.pdContactPerson}</div>}
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Parts Dept Contact Phone</label>
                                <input type="text" className={`form-control ${validationErrors.pdContactPhone ? 'is-invalid' : ''}`} value={clientData.pdContactPhone || ''} onChange={(e) => handleFieldChange("pdContactPhone", e.target.value)} />
                                {validationErrors.pdContactPhone && <div className="invalid-feedback">{validationErrors.pdContactPhone}</div>}
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Parts Dept Email</label>
                                <input type="email" className={`form-control ${validationErrors.pdContactEmail ? 'is-invalid' : ''}`} value={clientData.pdContactEmail || ''} onChange={(e) => handleFieldChange("pdContactEmail", e.target.value)} />
                                {validationErrors.pdContactEmail && <div className="invalid-feedback">{validationErrors.pdContactEmail}</div>}
                            </div>
                        </div>

                        <div className="row mb-2 px-3 py-2 bg-light rounded border mx-1">
                            <div className="col-md-3 py-2 d-flex align-items-center">
                                <div className="form-check form-switch p-0 m-0 d-flex align-items-center">
                                    <input className="form-check-input m-0" style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }} type="checkbox" id="chkShareInventory" checked={clientData.shareInventory || false} onChange={(e) => handleFieldChange("shareInventory", e.target.checked)} />
                                </div>
                                <label className="ms-2 mb-0" htmlFor="chkShareInventory" style={{ cursor: 'pointer' }}>Share Inventory</label>
                            </div>
                            <div className="col-md-3 py-2 d-flex align-items-center">
                                <div className="form-check form-switch p-0 m-0 d-flex align-items-center">
                                    <input className="form-check-input m-0" style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }} type="checkbox" id="chkAmDiscount" checked={clientData.allowAmDiscountOw || false} onChange={(e) => handleFieldChange("allowAmDiscountOw", e.target.checked)} />
                                </div>
                                <label className="ms-2 mb-0" htmlFor="chkAmDiscount" style={{ cursor: 'pointer' }}>Allow AM Discount Overwrite</label>
                            </div>
                            <div className="col-md-3 py-2 d-flex align-items-center">
                                <div className="form-check form-switch p-0 m-0 d-flex align-items-center">
                                    <input className="form-check-input m-0" style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }} type="checkbox" id="chkInstallKey" checked={clientData.installKeyRequired || false} onChange={(e) => handleFieldChange("installKeyRequired", e.target.checked)} />
                                </div>
                                <label className="ms-2 mb-0" htmlFor="chkInstallKey" style={{ cursor: 'pointer' }}>Install Key Required</label>
                            </div>
                            <div className="col-md-3 py-2 d-flex align-items-center">
                                <div className="form-check form-switch p-0 m-0 d-flex align-items-center">
                                    <input className="form-check-input m-0" style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }} type="checkbox" id="chkAutoReg" checked={clientData.autoRegisterEnabled || false} onChange={(e) => handleFieldChange("autoRegisterEnabled", e.target.checked)} />
                                </div>
                                <label className="ms-2 mb-0" htmlFor="chkAutoReg" style={{ cursor: 'pointer' }}>Auto Register Enabled</label>
                            </div>
                            <div className="col-md-3 py-2 d-flex align-items-center">
                                <div className="form-check form-switch p-0 m-0 d-flex align-items-center">
                                    <input className="form-check-input m-0" style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }} type="checkbox" id="chkAllowIncludeAll" checked={clientData.allowIncludeAll || false} onChange={(e) => handleFieldChange("allowIncludeAll", e.target.checked)} />
                                </div>
                                <label className="ms-2 mb-0" htmlFor="chkAllowIncludeAll" style={{ cursor: 'pointer' }}>Allow Include All</label>
                            </div>
                            <div className="col-md-3 py-2 d-flex align-items-center">
                                <div className="form-check form-switch p-0 m-0 d-flex align-items-center">
                                    <input className="form-check-input m-0" style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }} type="checkbox" id="chkLongLoadingPeriod" checked={clientData.longLoadingPeriod || false} onChange={(e) => handleFieldChange("longLoadingPeriod", e.target.checked)} />
                                </div>
                                <label className="ms-2 mb-0" htmlFor="chkLongLoadingPeriod" style={{ cursor: 'pointer' }}>Extended Loading Period</label>
                            </div>
                        </div>

                        <div className="row mb-3 mt-3 px-3 py-3 bg-light rounded border mx-1">
                            <h6 className="fw-bold text-secondary mb-3">Inventory Sources</h6>
                            <div className="d-flex flex-wrap gap-4">
                                {[
                                    { value: 0, label: 'INTERNAL' },
                                    { value: 2, label: 'SAME OWNER' },
                                    { value: 4, label: 'GROUP' },
                                    { value: 6, label: 'PK SHARED' },
                                    { value: 8, label: 'RECYCLER' },
                                    { value: 10, label: 'PAP' }
                                ].map(source => (
                                    <div key={source.value} className="form-check form-switch d-flex align-items-center">
                                        <input 
                                            className="form-check-input m-0" 
                                            style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }} 
                                            type="checkbox" 
                                            id={`chkSource_${source.value}`} 
                                            checked={clientData.inventorySources && clientData.inventorySources.includes(source.value)} 
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                let currentSources = clientData.inventorySources ? [...clientData.inventorySources] : [];
                                                if (checked && !currentSources.includes(source.value)) {
                                                    currentSources.push(source.value);
                                                } else if (!checked && currentSources.includes(source.value)) {
                                                    currentSources = currentSources.filter(v => v !== source.value);
                                                }
                                                handleFieldChange("inventorySources", currentSources);
                                            }} 
                                        />
                                        <label className="ms-2 mb-0" htmlFor={`chkSource_${source.value}`} style={{ cursor: 'pointer' }}>
                                            {source.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-5 pt-3 border-top text-end">
                            <button className="btn btn-light border shadow-sm px-4 me-3 fw-medium" style={{ borderRadius: '8px' }} onClick={resetClientDetails} disabled={loading || !originalClientData}>
                                <i className="bi bi-arrow-counterclockwise me-2"></i>Reset
                            </button>
                            <button className="btn-premium px-5 py-2 rounded-pill shadow-sm" onClick={saveClientDetails} disabled={loading}>
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                ) : (
                                    <i className="bi bi-check2-circle me-2"></i>
                                )}
                                {loading ? 'Saving...' : 'Save Configuration'}
                            </button>
                        </div>

                        {/* Nested Estimate File Path Manager */}
                        <div className="mt-5">
                            <EstimateFilePathManager clientId={selectedClientId} />
                        </div>

                        {/* Nested Public IP Config Manager */}
                        <div className="mt-5">
                            <ClientNetworkManager clientId={selectedClientId} />
                        </div>

                    </div>
                </div>
            )}

            {/* Client Selection Modal */}
            {showModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div className="modal-header border-bottom-0 py-3" style={{ background: 'var(--primary-gradient)', color: 'white' }}>
                                <h5 className="modal-title fw-bold"><i className="bi bi-search me-2"></i>Select Client</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body p-4 bg-light">
                                <div className="list-group">
                                    {clients.map(client => (
                                        <button
                                            key={client.oid}
                                            className="list-group-item list-group-item-action border-0 mb-2 shadow-sm rounded-3"
                                            onClick={() => selectClient(client.oid, client.clientName)}
                                        >
                                            <div className="d-flex w-100 justify-content-between align-items-center">
                                                <h6 className="mb-1 fw-bold text-primary">{client.clientName}</h6>
                                                <i className="bi bi-chevron-right text-muted"></i>
                                            </div>
                                            <small className="text-muted">{client.email} | {client.phone}</small>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Client Notifications Modal */}
            <Modal 
                show={showNotifModal} 
                onHide={() => setShowNotifModal(false)} 
                size="xl"
                dialogClassName="resizable-modal"
                contentClassName="border-0 shadow-lg rounded-4"
            >
                <Modal.Header closeButton className="border-bottom-0 py-3" style={{ background: 'var(--primary-gradient)', color: 'white' }} closeVariant="white">
                    <Modal.Title className="fw-bold"><i className="bi bi-bell-fill me-2"></i>Manage Notifications - {clientData?.clientName}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 p-md-5">
                    <div className="row">
                        <div className="col-md-5 border-end pe-4">
                            <h5 className="fw-bold text-secondary mb-4">Assigned Notifications</h5>
                            <div className="table-responsive" style={{ maxHeight: '400px' }}>
                                <Table size="sm" hover>
                                    <thead>
                                        <tr>
                                            <th>Notification Name</th>
                                            <th>Emails</th>
                                            <th className="text-end">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {notifsLoading ? (
                                            <tr><td colSpan="3" className="text-center">Loading...</td></tr>
                                        ) : clientNotifs.length === 0 ? (
                                            <tr><td colSpan="3" className="text-center text-muted">No notifications assigned</td></tr>
                                        ) : (
                                            clientNotifs.map(n => (
                                                <tr key={n.id}>
                                                    <td>{n.name}</td>
                                                    <td>
                                                        <small className="text-muted">{n.email || "No email defined"}</small>
                                                    </td>
                                                    <td className="text-end">
                                                        <Button 
                                                            variant="link" 
                                                            className="text-danger p-0" 
                                                            onClick={() => removeNotification(n.id)}
                                                            disabled={submittingAction === `remove-notif-${n.id}`}
                                                        >
                                                            {submittingAction === `remove-notif-${n.id}` ? (
                                                                <span className="spinner-border spinner-border-sm"></span>
                                                            ) : "Remove"}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                        <div className="col-md-7">
                            <h5>Available Notifications</h5>
                            <div className="table-responsive" style={{ maxHeight: '400px' }}>
                                <Table size="sm" hover>
                                    <thead>
                                        <tr>
                                            <th>Notification Name</th>
                                            <th>Target Emails (separated by ;)</th>
                                            <th className="text-end">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allNotifs.map(n => (
                                            <tr key={n.id}>
                                                <td>{n.name}</td>
                                                <td>
                                                    <Form.Control 
                                                        size="sm"
                                                        type="text"
                                                        placeholder="email1@test.com;email2@test.com"
                                                        value={notificationEmails[n.id] || ""}
                                                        onChange={(e) => setNotificationEmails(prev => ({ ...prev, [n.id]: e.target.value }))}
                                                        disabled={clientNotifs.some(cn => cn.id === n.id) || submittingAction === `add-notif-${n.id}`}
                                                    />
                                                </td>
                                                <td className="text-end">
                                                    <Button 
                                                        variant="outline-success" 
                                                        size="sm" 
                                                        onClick={() => addNotification(n.id)}
                                                        disabled={clientNotifs.some(cn => cn.id === n.id) || submittingAction === `add-notif-${n.id}`}
                                                    >
                                                        {submittingAction === `add-notif-${n.id}` ? (
                                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                                        ) : (clientNotifs.some(cn => cn.id === n.id) ? "Added" : "Add")}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowNotifModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PKClientManager;
